import { useGetCompanies } from '@services/companies'
import { useAllSessions } from '@services/session'
import dayjs from 'dayjs'
import moment from 'moment'
import { isNil } from 'ramda'
import React from 'react'
import {
  ChargingTypeEnum,
  useChargingFeeDayTypeEnum,
  useChargingFeeDiscountTargetEnum,
  useChargingFeeDiscountTypeEnum,
  useChargingFeePeakTypeEnumText,
  useChargingFeeStatusEnum,
} from './chargingFee.enum'
import {
  IChargingFeeData,
  IChargingFeeDiscountData,
  ICompanyChargingFeeDiscount,
  ICompanyChargingFeeDiscountRes,
} from './chargingFee.type'

export const useConvertToChargingFee = () => {
  const { byId } = useAllSessions()

  const { byId: byIdDayType } = useChargingFeeDayTypeEnum()
  const { toText: toTextPeakType } = useChargingFeePeakTypeEnumText()

  const converter = React.useCallback(
    (data: any) => {
      if (!data) {
        return null
      }

      const _convertChargingFeeTimeList = (time: any) => {
        time['dayTypeStr'] = byIdDayType[time.dayType]?.label
        time['peakTypeStr'] = toTextPeakType(time.peakType)
        time['timeRange'] = []
        time['times']?.forEach((x: any) => {
          x['timeStartStr'] = dayjs()
            .startOf('day')
            .add(x.timeStart, 'milliseconds')
            .format('HH:mm')
          x['timeEndStr'] = dayjs()
            .startOf('day')
            .add(x.timeEnd, 'milliseconds')
            .format('HH:mm')
          time['timeRange'].push(`${x['timeStartStr']}-${x['timeEndStr']}`)
        })
      }

      data.chargingFeeRuleList?.forEach((fee: any) => {
        fee.chargingFeeTimeList?.forEach(_convertChargingFeeTimeList)
      })
      data?.nextChargingFee?.chargingFeeRuleList?.forEach((fee: any) => {
        fee.chargingFeeTimeList?.forEach(_convertChargingFeeTimeList)
      })

      const currentChargeFee = {
        superFastChargeValues: data?.chargingFeeRuleList?.filter(
          (x: any) => x.chargingType === 0
        )?.[0],
        fastChargeValues: data?.chargingFeeRuleList?.filter(
          (x: any) => x.chargingType === 1
        )?.[0],
        slowChargeValues: data?.chargingFeeRuleList?.filter(
          (x: any) => x.chargingType === 2
        )?.[0],
      }

      const activedChargeFee = {
        ...currentChargeFee,
      }

      const prevChargeFee = {
        superFastChargeValues: null,
        fastChargeValues: null,
        slowChargeValues: null,
      }

      const nextChargingFeeRuleList =
        data?.nextChargingFee?.chargingFeeRuleList ?? []
      nextChargingFeeRuleList.forEach((fee: any) => {
        if (!fee.isUpdated) {
          return
        }
        if (fee.chargingType === 0) {
          prevChargeFee.superFastChargeValues =
            currentChargeFee.superFastChargeValues
          currentChargeFee.superFastChargeValues = fee
        } else if (fee.chargingType === 1) {
          prevChargeFee.fastChargeValues = currentChargeFee.fastChargeValues
          currentChargeFee.fastChargeValues = fee
        } else if (fee.chargingType === 2) {
          prevChargeFee.slowChargeValues = currentChargeFee.slowChargeValues
          currentChargeFee.slowChargeValues = fee
        }
      })

      return {
        ...data,
        sessionName: byId[data?.sessionId]?.name,
        ...currentChargeFee,
        prevChargeFee,
        activedChargeFee,
      } as IChargingFeeData
    },
    [byId]
  )

  return converter
}

export const useConvertToChargingFeeDiscount = () => {
  const { byId } = useAllSessions()

  const { byId: byIdDayType } = useChargingFeeDayTypeEnum()

  const { byId: byIdChargingFeeDiscountTarget } =
    useChargingFeeDiscountTargetEnum()

  const { byId: byIdChargingFeeDiscountType } = useChargingFeeDiscountTypeEnum()

  const { byId: byIdChargingFeeStatus } = useChargingFeeStatusEnum()

  const converter = React.useCallback(
    (data: any) => {
      if (!data) {
        return null
      }

      const _convertChargingFeeTimeList = (time: any) => {
        time['dayTypeStr'] = byIdDayType[time.dayType]?.label
        time['timeRange'] = []
        time['times']?.forEach((x: any) => {
          x['timeStartStr'] = dayjs()
            .startOf('day')
            .add(x.timeStart, 'milliseconds')
            .format('HH:mm')
          x['timeEndStr'] = dayjs()
            .startOf('day')
            .add(x.timeEnd, 'milliseconds')
            .format('HH:mm')
          time['timeRange'].push(`${x['timeStartStr']}-${x['timeEndStr']}`)
        })
      }

      data.chargingFeeDiscountRuleList?.forEach((fee: any) => {
        fee.chargingFeeTimeList?.forEach(_convertChargingFeeTimeList)
      })
      data?.nextChargingDiscount?.chargingFeeDiscountRuleList?.forEach(
        (fee: any) => {
          fee.chargingFeeTimeList?.forEach(_convertChargingFeeTimeList)
        }
      )

      const currentChargeFee = {
        superFastChargeValues: data?.chargingFeeDiscountRuleList?.filter(
          (x: any) => x.chargingType === ChargingTypeEnum.SUPER_FAST
        )?.[0],
        fastChargeValues: data?.chargingFeeDiscountRuleList?.filter(
          (x: any) => x.chargingType === ChargingTypeEnum.FAST
        )?.[0],
        slowChargeValues: data?.chargingFeeDiscountRuleList?.filter(
          (x: any) => x.chargingType === ChargingTypeEnum.SLOW
        )?.[0],
      }

      const activedChargeFee = {
        ...currentChargeFee,
      }

      const prevChargeFee = {
        superFastChargeValues: null,
        fastChargeValues: null,
        slowChargeValues: null,
      }

      const nextChargingFeeRuleList =
        data?.nextChargingDiscount?.chargingFeeDiscountRuleList ?? []

      nextChargingFeeRuleList.forEach((fee: any) => {
        if (!fee.isUpdated) {
          return
        }
        if (fee.chargingType === 0) {
          prevChargeFee.superFastChargeValues =
            currentChargeFee.superFastChargeValues
          currentChargeFee.superFastChargeValues = fee
        } else if (fee.chargingType === 1) {
          prevChargeFee.fastChargeValues = currentChargeFee.fastChargeValues
          currentChargeFee.fastChargeValues = fee
        } else if (fee.chargingType === 2) {
          prevChargeFee.slowChargeValues = currentChargeFee.slowChargeValues
          currentChargeFee.slowChargeValues = fee
        }
      })

      const _convertBasicValues = (values: any) => {
        return isNil(values)
          ? null
          : {
              ...values,
              sessionName: byId[values?.sessionId]?.name,
              targetStr: byIdChargingFeeDiscountTarget[values?.target]?.label,
              statusStr: byIdChargingFeeStatus[values?.status]?.label || '',
              datesStr:
                values?.startAt && values?.endAt
                  ? `${dayjs(values?.startAt).format('YYYY/MM/DD')} ~ ${dayjs(
                      values?.endAt
                    ).format('YYYY/MM/DD')}`
                  : '',
              dates:
                values?.startAt && values?.endAt
                  ? [moment(values?.startAt), moment(values?.endAt)]
                  : [undefined, undefined],
              discountTypeStr:
                byIdChargingFeeDiscountType[values?.discountType]?.label,
              targetStrs: values?.target?.map(
                (x: any) => byIdChargingFeeDiscountTarget[x]?.label
              ),
            }
      }

      return {
        ..._convertBasicValues(data),
        ...currentChargeFee,
        prevChargeFee,
        activedChargeFee,
        nextChargingDiscount: _convertBasicValues(data?.nextChargingDiscount),
      } as IChargingFeeDiscountData
    },
    [byId]
  )

  return converter
}

export const useCompanyChargingFeeDiscountConverter = () => {
  const { toFullPathName } = useGetCompanies()

  const converter = React.useCallback(
    (
      data?: ICompanyChargingFeeDiscountRes
    ): null | ICompanyChargingFeeDiscount => {
      return data
        ? {
            ...data,
            orgSessionName: toFullPathName(data.orgSessionId) || '-',
          }
        : null
    },
    [toFullPathName]
  )

  return converter
}
